import { useId, type JSX, type ReactNode } from 'react'

export interface StepItem {
  key: string
  label: string
  content: ReactNode
  /** Optional step number to display inside the circle. Defaults to the 1-based index. */
  step?: number
  /** Small secondary line under the label. */
  sublabel?: string
  /** Prevent this step from being selected. */
  disabled?: boolean
  /** PrimeIcons class, e.g. `pi pi-user`. Rendered in the circle. */
  icon?: string
  /** When true, the circle shows a checkmark — use to reflect that this step's required fields are filled in. */
  completed?: boolean
}

interface StepperSectionProps {
  steps: StepItem[]
  activeIndex: number
  onChange: (index: number) => void
  className?: string
}

export function StepperSection({
  steps,
  activeIndex,
  onChange,
  className = '',
}: StepperSectionProps): JSX.Element {
  const baseId = useId()

  return (
    <div className={`${className}`}>
      <div className="relative flex w-full items-start justify-between" role="tablist" aria-orientation="horizontal">
        {steps.map((step, index) => {
          const isActive = index === activeIndex
          const isCompleted = index < activeIndex || Boolean(step.completed)
          const isDisabled = Boolean(step.disabled)

          const circleBase =
            'flex h-10 w-10 items-center justify-center rounded-full border-2 text-sm font-semibold transition-colors'
          let circleTone = isCompleted
            ? 'border-[var(--kc3-accent)] bg-[var(--kc3-accent)] text-white'
            : isActive
              ? 'border-[var(--kc3-accent)] bg-white text-[var(--kc3-accent)]'
              : 'border-gray-300 bg-white text-gray-500'
          if (isDisabled) {
            circleTone = 'border-gray-200 bg-gray-100 text-gray-400'
          }

          const labelTone = isDisabled
            ? 'text-gray-400'
            : isCompleted
              ? 'text-[var(--kc3-accent)] font-semibold'
              : isActive
                ? 'text-gray-900 font-semibold'
                : 'text-gray-500'

          const showConnector = index < steps.length - 1
          const connectorFilled = index < activeIndex

          const stepNumber = step.step !== undefined ? step.step : index + 1

          return (
            <div key={step.key} className="group flex flex-1 flex-col items-center">
              <div className="relative flex w-full items-center">
                {showConnector && (
                  <div
                    className="absolute left-[calc(50%+1.25rem)] top-1/2 h-0.5 w-[calc(100%-2.5rem)] -translate-y-1/2"
                    aria-hidden="true"
                  >
                    <div
                      className={`h-full w-full ${
                        connectorFilled ? 'bg-[var(--kc3-accent)]' : 'bg-gray-200'
                      }`}
                    />
                  </div>
                )}
                <button
                  id={`${baseId}-step-${step.key}`}
                  type="button"
                  role="tab"
                  aria-label={step.label}
                  aria-selected={isActive}
                  aria-controls={`${baseId}-panel-${step.key}`}
                  aria-disabled={isDisabled}
                  tabIndex={isActive ? 0 : -1}
                  disabled={isDisabled}
                  onClick={() => !isDisabled && onChange(index)}
                  className={`${circleBase} ${circleTone} relative z-10 mx-auto focus:outline-none focus:ring-2 focus:ring-[var(--kc3-accent)] focus:ring-offset-2 disabled:cursor-not-allowed`}
                >
                  {isCompleted ? (
                    <i className="pi pi-check text-xs" aria-hidden="true" />
                  ) : step.icon ? (
                    <i className={step.icon} aria-hidden="true" />
                  ) : (
                    stepNumber
                  )}
                </button>
              </div>
              <span
                className={`mt-2 text-center text-sm ${labelTone}`}
                onClick={() => !isDisabled && onChange(index)}
                role="button"
                aria-disabled={isDisabled}
                tabIndex={-1}
              >
                {step.label}
              </span>
              {step.sublabel && (
                <span className="text-center text-xs text-gray-400">{step.sublabel}</span>
              )}
            </div>
          )
        })}
      </div>

      <div className="mt-8">
        {steps.map((step, index) => {
          const isActive = index === activeIndex
          return (
            <div
              key={step.key}
              id={`${baseId}-panel-${step.key}`}
              role="tabpanel"
              aria-labelledby={`${baseId}-step-${step.key}`}
              hidden={!isActive}
            >
              {step.content}
            </div>
          )
        })}
      </div>
    </div>
  )
}
